import type { ZodIssue } from 'zod';
import { createInternalUnitCode } from '@/lib/utils/unitIdentifiers';
import { roundCurrency } from '@/lib/utils/currency';
import { formatRentNumberToInput } from './rentInput';
import type {
  FloorLevelCode,
  UnitAvailabilityStatus,
  UnitFormValues,
  UnitPossessionTimeline,
  UnitSummary,
} from '@/lib/types/property';

export type UnitFormState = {
  unitCode: string;
  unitLabel: string;
  floorLevel: FloorLevelCode | '';
  areaSf: string;
  minDivisibleSf: string;
  availabilityStart: string;
  availabilityStatus: UnitAvailabilityStatus;
  possessionTimeline: UnitPossessionTimeline;
  netRentPerSf: string;
  additionalRentPerSf: string;
  incentives: string;
  potentiallyDivisible: boolean;
  contactEmail: string;
  displayOnPublicWebsite: boolean;
  published: boolean;
  heroImageUrl: string;
  galleryImageUrls: string[];
};

export type UnitFormErrors = Partial<Record<keyof UnitFormState, string>>;

export type UnitFormLayout = 'desktop' | 'tablet' | 'mobile';

export const UNIT_STATUS_OPTIONS: UnitAvailabilityStatus[] = ['available', 'unavailable', 'leased'];

export const UNIT_POSSESSION_TIMELINE_OPTIONS: UnitPossessionTimeline[] = [
  'immediate',
  'negotiable',
];

const NUMBERED_FLOORS: FloorLevelCode[] = Array.from(
  { length: 30 },
  (_value, index) => String(index + 1) as FloorLevelCode,
);

export const FLOOR_LEVEL_OPTIONS: FloorLevelCode[] = (
  ['B3', 'B2', 'B1', 'G', 'M'] as FloorLevelCode[]
).concat(NUMBERED_FLOORS);

export function createUnitInitialState(unit: UnitSummary): UnitFormState {
  return {
    unitCode: unit.unitCode,
    unitLabel: unit.unitLabel,
    floorLevel: unit.floorLevel ?? '',
    areaSf: toInput(unit.areaSf),
    minDivisibleSf: unit.minDivisibleSf != null ? toInput(unit.minDivisibleSf) : '',
    availabilityStart: toDateInput(unit.availabilityStart),
    availabilityStatus: unit.availability.status,
    possessionTimeline: unit.possessionTimeline,
    netRentPerSf: formatRentNumberToInput(unit.rent.netRentPerSf),
    additionalRentPerSf: formatRentNumberToInput(unit.rent.additionalRentPerSf),
    incentives: unit.incentives ?? '',
    potentiallyDivisible: unit.potentiallyDivisible,
    contactEmail: unit.contactEmail,
    displayOnPublicWebsite: unit.displayOnPublicWebsite,
    heroImageUrl: unit.heroImageUrl ?? '',
    galleryImageUrls: unit.galleryImageUrls,
    published: unit.published,
  } satisfies UnitFormState;
}

export function createUnitEmptyState(): UnitFormState {
  return {
    unitCode: createInternalUnitCode(),
    unitLabel: '',
    floorLevel: '',
    areaSf: '',
    minDivisibleSf: '',
    availabilityStart: '',
    availabilityStatus: 'available',
    possessionTimeline: 'immediate',
    netRentPerSf: '',
    additionalRentPerSf: '',
    incentives: '',
    potentiallyDivisible: false,
    contactEmail: '',
    displayOnPublicWebsite: true,
    heroImageUrl: '',
    galleryImageUrls: [],
    published: true,
  } satisfies UnitFormState;
}

export function buildUnitPayload({
  propertyId,
  state,
  unit,
}: {
  propertyId: string;
  state: UnitFormState;
  unit?: UnitSummary;
}): UnitFormValues {
  const netRent = roundCurrency(Number(state.netRentPerSf));
  const additionalRent =
    state.additionalRentPerSf.trim().length > 0
      ? roundCurrency(Number(state.additionalRentPerSf))
      : null;
  return {
    id: unit?.id,
    propertyId,
    unitCode: state.unitCode.trim(),
    unitLabel: state.unitLabel.trim(),
    floorLevel: normalizeFloorLevel(state.floorLevel),
    areaSf: Number(state.areaSf),
    minDivisibleSf: state.minDivisibleSf.trim().length > 0 ? Number(state.minDivisibleSf) : null,
    availabilityStart: normalizeAvailabilityStart(state.availabilityStart, unit?.availabilityStart),
    availabilityStatus: state.availabilityStatus,
    possessionTimeline: state.possessionTimeline,
    rentBasis: unit?.rent.rentBasis ?? 'net',
    netRentPerSf: netRent,
    additionalRentPerSf: additionalRent,
    rentFrequency: 'annually',
    incentives: state.incentives.trim().length > 0 ? state.incentives.trim() : null,
    potentiallyDivisible: state.potentiallyDivisible,
    contactEmail: state.contactEmail.trim(),
    heroImageUrl: state.heroImageUrl.trim().length > 0 ? state.heroImageUrl.trim() : null,
    galleryImageUrls: state.galleryImageUrls,
    planGeometry: unit?.planGeometry ?? null,
    displayOnPublicWebsite: state.displayOnPublicWebsite,
    published: state.published,
  } satisfies UnitFormValues;
}

export function mapUnitIssues(issues: ZodIssue[]): UnitFormErrors {
  const next: UnitFormErrors = {};
  for (const issue of issues) {
    const field = issue.path.at(0);
    if (typeof field === 'string' && next[field as keyof UnitFormState] === undefined) {
      next[field as keyof UnitFormState] = issue.message;
    }
  }
  return next;
}

function toInput(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

function normalizeFloorLevel(value: FloorLevelCode | ''): FloorLevelCode | null {
  return value === '' ? null : value;
}

function normalizeAvailabilityStart(value: string, fallback?: string | null): string | null {
  const trimmed = value.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  return typeof fallback === 'string' && fallback.length > 0 ? fallback : null;
}

function toDateInput(value: string | null | undefined): string {
  if (typeof value !== 'string' || value.length === 0) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
}
